<template>
  <div class="large-12">
    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h3>Stichtag</h3>
        <div v-if="!showStyle" class="spv-select-group flex-no-wrap row">
          <div
            v-for="d in deadlines"
            :key="d.id"
            :class="`small-6 medium-4 large-4 columns`"
          >
            <div class="radio-input">
              <input
                :id="`climate-${ d.id }`"
                :checked="deadline_id == d.id"
                :value="d.id"
                type="radio"
                name="climate-deadline"
                @change="onSelectDeadline(d.id)"
              >
              <label :class="{ 'checked': deadline_id == d.id }" :for="`climate-${ d.id }`">
                <p>{{ d.name }}</p>
              </label>
            </div>
          </div>
        </div>

        <div v-else class="spv-select-group">
          <div class="small-12 medium-6 large-4">
            <select :value="deadline_id" class="spv-input-select" @change="(e) => onSelectDeadline(e.target.value)">
              <option v-for="d in deadlines" :key="d.id" :value="d.id">
                {{ d.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-6 columns">
        <h3>Zeitraum</h3>
        <div class="large-8">
          <select :value="year_" class="spv-input-select" @change="(e) => onSelectYear(e.target.value)">
            <option v-for="y in years" :value="y" :key="y">
              {{
                deadline_id === 'j'
                  ? `Jun ${ y } - Mai ${ y + 1 }`
                  : `Dez ${ y } - Nov ${ y + 1 }`
              }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Normierte Wertentwicklung Climate Objective Index {{ deadline_.name }}</h1>
        <div class="large-12 columns spv-chart-wrapper">
          <index-chart
            v-show="deadline_id === 'j'"
            :data="chart_items['j'] || []"
            :max="chart_max['j'] || 0"
            name="Climate Objective Index"
            color="#E81700"
          />

          <index-chart
            v-show="deadline_id === 'd'"
            :data="chart_items['d'] || []"
            :max="chart_max['d'] || 0"
            name="Climate Objective Index"
            color="#E81700"
          />

          <div class="spv-price-inquiry">
            <b class="spv-price-inquiry__label">Kursabfrage</b>

            <div class="spv-price-inquiry__date-picker">
              <span>Datum</span>
              <date-picker
                :min="min_date"
                :max="max_date"
                :value="inquiry_date"
                @select="onSelectInquiryDate"
              />
            </div>

            <div class="spv-price-inquiry__value">
              <span v-if="!inquiry_valid && !inquiry_date" class="error-value">
                Bitte wählen Sie ein Datum
              </span>
              <span v-else-if="inquiry_value === -1" class="error-value">
                Für dieses Datum liegt kein Datensatz vor. Bitte wählen Sie ein anderes Datum aus
              </span>
              <span v-else-if="!!inquiry_value">
                Climate Objective Index&nbsp;&nbsp;<b>{{ inquiry_value }} Punkte</b>
              </span>
            </div>

            <a class="button button-small block" @click="loadDayValue">Anzeigen</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="configText" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div v-html="configText" />
      </div>
    </div>

    <div v-if="show_data" class="row wrapper-spacing">
      <div v-if="details_data.index_values && details_data.index_values.length" class="medium-12 large-12 columns">
        <h1>
          <div class="spv-info-wrapper info-trend">
            Monatliche
            <div class="info-overlay">
              Wertentwicklung
              <div class="info-overlay__icon">
                <i class="fo icon-info_kreis" />
              </div>
              <div class="info-overlay__text" >
                {{ gloss_texts_trend[0] }}
              </div>
            </div>
          </div>
          <div class="spv-info-wrapper text-only">
            Climate Objective Index {{ deadline_.name }}
          </div>
          <div class="spv-info-wrapper info-trend">
            <span class="text-normal">mit Zielvolatilität</span>
            <div class="info-overlay">
              <span class="text-normal">{{ targetVol }}</span>
              <div class="info-overlay__icon">
                <i class="fo icon-info_kreis" />
              </div>
              <div class="info-overlay__text" >
                {{ gloss_texts_trend[1] }}
              </div>
            </div>
          </div>
        </h1>

        <div class="spv-table-wrapper">
          <table class="spv-product-table spv-index-table">
            <thead>
              <tr>
                <th class="medium-8 large-9">
                  Indexstichtag<br>{{ deadline_.label }}{{ details_data.year }}
                </th>
                <th class="medium-4 large-3">
                  Indexstand<br>jeweils am<br>Ende des Monats
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(data, index) in details_data.index_values" :key="index">
                <td>{{ formatDate(data.date) }}</td>
                <td>{{ formatMoney(data.index_value, 2) }}</td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td class="td-padding">
                  <div class="spv-info-wrapper">
                    Jahresrendite
                    <div class="info-overlay">
                      <div class="info-overlay__icon">
                        <i class="fo icon-info_kreis" />
                      </div>
                      <div class="info-overlay__text right-auto" >
                        {{ gloss_texts_trend[3] }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ formatPercent(details_data.yearly_yield, 2) }}</td>
              </tr>

              <tr>
                <td>
                  <div class="spv-info-wrapper">
                    Maßgebliche
                    <div class="info-overlay">
                      Jahresrendite
                      <div class="info-overlay__icon">
                        <i class="fo icon-info_kreis" />
                      </div>
                      <div class="info-overlay__text right-auto" >
                        {{ gloss_texts_trend[4] }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ formatPercent(details_data.decisive_yearly_yield, 2) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="pdf-button-wrapper">
          <a class="button button-small" @click="onCreatePdf">
            Drucken
          </a>
        </div>
      </div>

      <div v-else class="medium-12 large-12 columns">
        <h1 class="no-margin">Keine Monatswerte für Climate Objective Index für {{ deadline_.name }} vorhanden.</h1>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Zusammensetzung der Aktien des Climate Objective Index {{ deadline_.name }}</h1>
        <p><b>Stand {{ `${ assets_month_name } ${ assets_year }` }}</b></p>
        <p v-html="configTextOfAsset" />
      </div>

      <div class="small-12 medium-12 large-12 columns chart-spacing">
        <n-accordion-chart :items="accordion_chart_items" />
      </div>
    </div>

    <div v-if="configText60" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <br>
        <h2>Details zum Indexportfolio</h2>
        <div v-html="configText60" />
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="small-12 medium-12 large-12 columns">
        <div class="spv-table-wrapper">
          <input
            :class="`climate-asset-filter`"
            :type="`text`"
            :placeholder="`Suche nach Name, Land oder Industrie`"
            :value="asset_filter"
            @input="(e) => asset_filter = e.target.value"
          >

          <table class="spv-product-table spv-index-table spv-index-table--climate">
            <thead>
              <tr>
                <th>Name</th>
                <th>Land</th>
                <th>Industrie</th>
                <th>Gewichtung</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(asset, index) in assets_" :key="index">
                <td>{{ asset.name }}</td>
                <td>{{ asset.country }}</td>
                <td>{{ asset.industry }}</td>
                <td>{{ formatPercent(asset.weight, 2) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="assets.length > 10" class="text-center">
            <a v-if="!asset_filter" class="toggle-expand" @click="show_all_rows = !show_all_rows">
              <i v-if="!show_all_rows" class="fo fo-arrow-down" />
              <i v-else class="fo fo-arrow-up" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="factsheet_path && factsheet_path[deadline_id]" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div>
          <a class="button call-to-action" @click.stop="onOpenFactsheet(factsheet_path[`${ deadline_id }_path`])">
            Download Factsheet
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import mixin from './mixin';
import NAccordionChart from '../../components/common/n-accordion-chart.vue';

import { b64DecodeUnicode } from '@/modules/utils';

export default {
  name: 'index_spv_climate',

  components: {
    NAccordionChart
  },

  mixins:[
    mixin
  ],

  data: () => ({
    deadline_id: 'j',

    deadlines: [
      {
        id: 'j',
        name: 'Juni',
        label: '01.06.'
      },
      {
        id: 'd',
        name: 'Dezember',
        label: '01.12.',
      }
    ],

    index_config: null,
    config_values: null,
    factsheet_path: null,
    assets: [],
    assets_month: 0,
    assets_year: '',
    daily_data: [],
    show_data: false,

    details_data: [],
    chart_items: {},
    chart_max: {},

    asset_filter: '',
    show_all_rows: false,

    info_texts: {
      'Energie': 'Entdecker, Raffinerien, Vermarkter und Vertreiber von fossilen Brennstoffen, Uran und erneuerbaren Energien, Hersteller energiebezogener Geräte und Anbieter unterstützender Dienstleistungen',
      'Finanzen': 'Betreiber von Privat-, Geschäfts- und Investmentbanken, Investment Trusts und Finanzmärkten sowie Anbieter von Investment- und Versicherungsdienstleistungen.',
      'Gesundheitswesen': 'Hersteller, Entwickler und Vermarkter von Pharmazeutika, medizinischen Geräten und Verbrauchsmaterialien sowie fortschrittlichen therapeutischen Behandlungen und Geräten sowie Anbieter von Gesundheitseinrichtungen und medizinischer Forschung und Entwicklung.',
      'Grundeigentum': 'Unternehmen, die sich mit der Entwicklung und / oder Verwaltung von Immobiliengeschäften und damit verbundenen Dienstleistungen befassen, gewerbliche, diversifizierte und spezialisierte (Gesundheitswesen, Gastgewerbe, Selbstlagerung, Holz, Hypothek und islamische) Immobilieninvestmentfonds.',
      'Grundmaterialien': 'Hersteller, Extraktoren und Raffinerien von Chemikalien, Mineralien, Edelmetallen, Stahl, Aluminium, Forstprodukten sowie Bau- und anderen Rohstoffen.',
      'Industriell': 'Hersteller von Industrieanlagen und Handelsbedarf, Anbieter verwandter Dienstleistungen wie diversifizierter Handel, Vertriebs- und Transportdienstleistungen sowie Anbieter professioneller Informationsdienste.',
      'Nichtzyklische Verbraucher': 'Hersteller, Verarbeiter und Einzelhändler von Lebensmitteln, Tabak und Getränken sowie Hersteller von Haushalts- und persönlichen Produkten sowie Anbieter persönlicher Dienstleistungen.',
      'Technologie': 'Hersteller von Halbleitern, Kommunikationsgeräten, Computerhardware und technologiebezogenen Bürogeräten, Anbieter von Beratung, IT-Dienstleistungen und Finanztechnologie (Fintech) sowie Infrastruktur. Eigentümer und Betreiber von Festnetz- und drahtlosen Telekommunikationsnetzen, die Sprach- und Datendienste anbieten.',
      'Verbraucherzyklika': 'Hersteller von Automobilen, Haushaltswaren, Textilien und anderen Produkten sowie Bauherren und Einzelhändler und Anbieter von Verbraucherdienstleistungen wie Hotel-, Unterhaltungs- und Mediendienstleistungen.',
      'Institutionen, Verbände & Organisationen': 'Institutionen, Verbände und Organisationen, die sich mit der Verwaltung und Förderung der Interessen und Werte ihrer Mitglieder befassen.',
      'Regierungsaktivität': 'Organisationen, die sich mit regierungsbezogenen Aktivitäten befassen. Dazu gehören öffentliche Finanzen, Polizei, Feuerwehr, Rechtswesen, Verwaltung, nationale Sicherheit und internationale Angelegenheiten.',
      'Akademische und pädagogische Dienstleistungen': 'Organisationen, die sich mit der Bereitstellung, Verwaltung und Leitung akademischer Kurse, nicht-akademischer Ausbildung und Schulung befassen.',
      'Dienstprogramme': 'Produzenten und Vertreiber von Strom, Erdgas, Wasser und Dampf, unter anderem Versorgungsunternehmen.',
    }
  }),

  computed: {
    assets_ () {
      if (!this.asset_filter) {
        return this.show_all_rows
          ? this.assets
          : this.assets.slice(0, 10);
      }

      const search_str = this.asset_filter.toLowerCase();

      return this.assets.filter(a => (
        a.name.toLowerCase().search(search_str) >= 0 ||
        a.country.toLowerCase().search(search_str) >= 0 ||
        a.industry.toLowerCase().search(search_str) >= 0
      ));
    },

    assets_month_name () {
      if (!this.assets_month) {
        return '';
      }

      return this.month_names[this.assets_month - 1];
    },

    deadline_ () {
      return this.deadlines.find(d => d.id === this.deadline_id);
    },

    years () {
      if (!this.index_config) {
        return [];
      }

      return (this.index_config[this.deadline_id].years || [])
        .sort((a, b) => b - a);
    },

    max_date () {
      if (!this.index_config) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_config[this.deadline_id].min_max.max_date;
    },

    min_date () {
      if (!this.index_config) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_config[this.deadline_id].min_max.min_date;
    },

    accordion_chart_items () {
      const items = [];
      let sum_weight = 0;

      this.assets.forEach(a => {
        const id = items.findIndex(i => i.label === a.industry);
        sum_weight += a.weight;

        if (id >= 0) {
          items[id].value += a.weight;
          return;
        }

        items.push({
          label: a.industry,
          percent: 0,
          value: a.weight
        });
      });

      const children = (item) => this.assets
        .filter(a => a.industry === item.label)
        .sort((a, b) => a.weight < b.weight ? 1 : -1)
        .map(a => ({
          label: a.name,
          percent: (a.weight / item.value) * 100,
          // value: a.weight
        }));

      return items
        .sort((a, b) => a.value < b.value ? 1 : -1)
        .map(item => ({
          label: item.label,
          percent: (item.value / sum_weight) * 100,
          info_text: this.info_texts[item.label],
          children: children(item)
        }));
    },

    configText() {
      if (!this.config_values) {
        return '';
      }

      return this.deadline_id === 'd'
        ? b64DecodeUnicode(this.config_values.climate_einleitung_dezember.value)
        : b64DecodeUnicode(this.config_values.climate_einleitung_juni.value);
    },

    configText60() {
      if (!this.config_values) {
        return '';
      }

      return this.deadline_id === 'd'
        ? b64DecodeUnicode(this.config_values.climate_beschreibung_60_aktien_dezember.value)
        : b64DecodeUnicode(this.config_values.climate_beschreibung_60_aktien_juni.value);
    },

    configTextOfAsset() {
      if (!this.config_values) {
        return '';
      }

      return this.deadline_id === 'd'
        ? b64DecodeUnicode(this.config_values.climate_asset_dezember.value)
        : b64DecodeUnicode(this.config_values.climate_asset_juni.value);
    },

    targetVol() {
      return this.details_data && this.details_data.target_vol
        ? this.formatPercent(this.details_data.target_vol * 100, 2)
        : '0,00%';
    }
  },

  methods: {
    loadIndexConfig () {
      if (!this.apiRequest || this.years.length > 0) {
        return;
      }

      this.apiRequest
        .get(this.config.paths.climate_years)
        .then(res => {
          this.config_values = res.data.config_values;
          this.index_config = res.data.index_data;
          this.show_data = res.data.show_data;
          this.factsheet_path = {
            j: res.data.factsheet_filename_jun,
            j_path: res.data.factsheet_path_jun,
            d: res.data.factsheet_filename_dec,
            d_path: res.data.factsheet_path_dec,
          };
          this.assets_month = res.data.assets_month;
          this.assets_year = res.data.assets_year;

          this.year_ = this.years[0];
          this.inquiry_date = this.max_date;
          this.assets = (res.data.assets || [])
            .map(a => ({
              ...a,
              weight: Math.round(a.weight * 100) / 100
            }))
            .sort((a, b) => b.weight - a.weight);

          this.deadlines.forEach(d => {
            this.loadDailyData(d.id);
          });

          this.loadDetails();
        });
    },

    loadDailyData (type) {
      if (!this.apiRequest || !!this.chart_items[this.deadline_id]) {
        return;
      }

      const params = {
        type
      };

      this.apiRequest
        .get(this.config.paths.climate_daily, {
          params
        })
        .then(res => {
          this.setChartData(type, res.data || []);
        });
    },

    loadDetails () {
      if (!this.show_data) {
        return;
      }

      let year = parseInt(this.year_);

      if (!year || isNaN(year)) {
        return;
      }

      const params = {
        type: this.deadline_id,
        effektive_year: year,
      };

      this.apiRequest
        .get(this.config.paths.climate_query, {
          params
        })
        .then(res => {
          this.details_data = res.data || {};
        });
    },

    loadDayValue() {
      if (!this.inquiry_date) {
        this.inquiry_value = '';
        this.inquiry_valid = false;
        return;
      }

      this.inquiry_valid = true;

      let params = {
        type: this.deadline_id,
        date: this.formatDate(this.inquiry_date, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
      };

      this.apiRequest
        .get(this.config.paths.climate_day, {
          params
        })
        .then(res => {
          this.inquiry_value = res.data.index_value
            ? this.formatMoney(res.data.index_value, 2)
            : -1;
        })
        .catch(err => {
          console.error(err);
          this.inquiry_value = -1;
        });
    },

    onSelectDeadline (value) {
      this.deadline_id = value;
      this.inquiry_value = '';
      this.inquiry_valid = false;
      this.inquiry_date = this.max_date;

      this.loadDetails();
    },

    setChartData (type, data) {
      this.chart_max[type] = 0;

      this.chart_items[type] = data
        .sort((a, b) => a.date > b.date ? 1 : -1)
        .map(item => {
          let value = parseFloat(item.index_value);

          if (value > this.chart_max[type]) {
            this.chart_max[type] = value;
          }

          return [
            moment(item.date).valueOf(),
            value
          ];
        });

      this.chart_items = {
        ...this.chart_items
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/apps/index/spv_climate.vue